// Google OAuth Callback Page Component
// Handles the redirect from Google after authentication

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../../api/auth';
import { organizationAPI } from '../../api/organizations';
import { TokenService } from '../../auth/services/tokenService';
import { persistBodyTokens } from '../../auth/utils/bodyTokenStorage';
import { authBB } from '../../utils/authBlackBox';
import styles from './GoogleCallback.module.css';

import { logger } from '../../utils/logger';

const GoogleCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  // Prevent duplicate API calls in React Strict Mode
  const hasProcessedCallback = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent duplicate calls (React Strict Mode runs effects twice in dev)
      if (hasProcessedCallback.current) {
        logger.log('🛑 Callback already processed, skipping duplicate call');
        return;
      }

      hasProcessedCallback.current = true;
      logger.log('✅ Processing Google OAuth callback');

      const code = searchParams.get('code');
      const error = searchParams.get('error');
      const state = searchParams.get('state');

      // Check for errors from Google
      if (error) {
        // This is the literal "abandoned" flow (user clicked Cancel on
        // Google's consent screen -> error=access_denied), not just a
        // failure -- it never reaches the try/catch below, so it needs its
        // own clear or a stale invitation_token here would hijack the next,
        // unrelated Google sign-in in this tab.
        sessionStorage.removeItem('invitation_token');
        setStatus('error');
        if (error === 'access_denied') {
          setErrorMessage('You cancelled the Google sign-in.');
        } else {
          setErrorMessage(`Google sign-in failed: ${error}`);
        }
        return;
      }

      // Check for authorization code
      if (!code) {
        sessionStorage.removeItem('invitation_token');
        setStatus('error');
        setErrorMessage('No authorization code received from Google.');
        return;
      }

      // Validate state for CSRF protection
      const savedState = sessionStorage.getItem('google_oauth_state');
      if (state && savedState && state !== savedState) {
        sessionStorage.removeItem('invitation_token');
        setStatus('error');
        setErrorMessage('Security validation failed. Please try again.');
        return;
      }

      // Clear the saved state
      sessionStorage.removeItem('google_oauth_state');

      // Clear any stale tokens before exchanging the code. The callback will
      // store fresh tokens on success, and this prevents stale credentials from
      // leaking into subsequent API calls. Tagged 'oauth_scrub' so the
      // black-box records this routine wipe as benign — it runs on EVERY
      // successful Google login and must not masquerade as a session kick.
      TokenService.clearTokens('oauth_scrub');

      try {
        // Exchange code for tokens
        logger.log('🔄 Exchanging authorization code for tokens...');

        // Check if this Google sign-in is part of an invitation flow
        const invitationToken = sessionStorage.getItem('invitation_token');
        let response: { access_token?: string; refresh_token?: string; is_new_user?: boolean };

        // "Remember me" choice stashed by LoginForm before the Google redirect.
        // Controls the backend refresh-token lifetime (30d vs 7d).
        const rememberMe = sessionStorage.getItem('google_oauth_remember_me') === 'true';
        sessionStorage.removeItem('google_oauth_remember_me');

        if (invitationToken) {
          logger.log('🔄 Invitation flow detected — using invitation Google endpoint...');
          const redirectUri = `${window.location.origin}/auth/google/callback`;
          response = await organizationAPI.registerWithInvitationGoogle(invitationToken, {
            code,
            redirect_uri: redirectUri,
          });
          sessionStorage.removeItem('invitation_token');
        } else {
          response = await authAPI.handleGoogleCallback(code, rememberMe);
        }

        // Store tokens via TokenService (always localStorage — the app-wide
        // single source of truth; see auth-truth.md). The rememberMe flag is
        // recorded for UI/metadata; session lifetime itself is enforced by the
        // backend refresh-token expiry.
        // Using persistBodyTokens instead of raw localStorage.setItem prevents
        // the historical split where TokenService and direct-localStorage
        // readers disagreed — which caused apiService to send requests without
        // auth, triggering a spurious refresh cycle that clobbered localStorage
        // and fired the "Session expired" toast even though /me returned 200.
        const stored = persistBodyTokens(response, rememberMe);
        authBB('login_success', {
          flow: 'google',
          rememberMe,
          bodyTokensStored: stored,
          responseHadAccess: !!response.access_token,
          responseHadRefresh: !!response.refresh_token,
        });

        logger.log('✅ Tokens stored, triggering auth state change...');

        // Trigger auth context to re-initialize with new tokens
        window.dispatchEvent(new CustomEvent('auth:state_changed'));

        // Small delay to allow AuthContext to initialize
        setTimeout(() => {
          let from: unknown;
          try {
            const stored = sessionStorage.getItem('auth_google_return');
            if (stored) from = JSON.parse(stored);
          } catch {
            // A malformed stale return path falls back to the normal destination.
          }
          sessionStorage.removeItem('auth_google_return');
          navigate(invitationToken ? '/dashboard' : '/auth/continue', {
            replace: true,
            state: invitationToken ? undefined : { from },
          });
        }, 500);
      } catch (err: any) {
        sessionStorage.removeItem('invitation_token');
        logger.error('Google auth error:', err);
        setStatus('error');
        setErrorMessage(err.message || 'Failed to complete Google sign-in.');
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  if (status === 'error') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.errorIcon}>❌</div>
          <h1 className={styles.title}>Sign-in Failed</h1>
          <p className={styles.message}>{errorMessage}</p>
          <button
            className={styles.button}
            onClick={() => navigate('/login', { replace: true })}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.spinner} />
        <h1 className={styles.title}>Signing you in...</h1>
        <p className={styles.message}>Please wait while we complete your Google sign-in.</p>
      </div>
    </div>
  );
};

export default GoogleCallback;

